import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Avatar, Box, Button, Typography, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getRelationForTicketsAsync, assignTicketFromDatabaseAsync } from '@application/features/tickets/ticketsThunk';
import { toast } from 'react-toastify';
import { getSuggestedPeopleForPartnerAsync } from '@application/features/partner/partnerThunk';
import StyledSearchInput from '@ui/components/StyledSearchInput';
const initialRows = [
    { id: '1', name: 'Mark Veerman', title: 'CIO', company: 'Smit Elektrotechniek', email: 'mark.veerman@smitelektro.nl', assigned: true },
    { id: '2', name: 'Mark Veerman', title: 'CIO', company: 'Smit Elektrotechniek', email: 'mark.veerman@smitelektro.nl', assigned: false },
    { id: '3', name: 'Mark Veerman', title: 'CIO', company: 'Smit Elektrotechniek', email: 'mark.veerman@smitelektro.nl', assigned: false },
    { id: '4', name: 'Mark Veerman', title: 'CIO', company: 'Smit Elektrotechniek', email: 'mark.veerman@smitelektro.nl', assigned: true }
];
const DatabaseGrid = ({ ticketId, onClose }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { id, eventId } = useParams();
    const { ticketrelation } = useSelector((state) => state.tickets);
    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay
        return () => clearTimeout(timer);
    }, [searchTerm]);
    useEffect(() => {
        if (eventId) {
            const request = {
                eventId: eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: debouncedSearchTerm || null
            };
            dispatch(getRelationForTicketsAsync(request));
        }
    }, [dispatch, eventId, paginationModel, debouncedSearchTerm]);
    const columns = [
        {
            field: 'person',
            headerName: t('sponsorships.tickets.assign.columns.person') || 'Person',
            flex: 1,
            minWidth: 240,
            sortable: false,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, children: [_jsx(Avatar, { variant: 'rounded', sx: { width: 44, height: 44, fontSize: 13 }, children: String(params.row.name).split(' ').map((n) => n[0]).slice(0, 2).join('') }), _jsxs(Box, { minWidth: 0, sx: { overflow: 'hidden', width: '100%' }, children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', sx: { fontWeight: 500 }, noWrap: true, children: params.row.name }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', noWrap: true, children: params.row.title }), params.row.email && (_jsx(Tooltip, { title: params.row.email, arrow: true, placement: "top", children: _jsx(Typography, { variant: 'caption', color: 'text.secondary', noWrap: true, sx: {
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%',
                                        cursor: 'pointer'
                                    }, children: params.row.email }) }))] })] }))
        },
        {
            field: 'company',
            headerName: t('sponsorships.tickets.assign.columns.company') || 'Company',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: params.row.company }))
        },
        {
            field: 'type',
            headerName: t('sponsorships.tickets.assign.columns.type') || 'Type',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: params.row.type }))
        },
        {
            field: 'action',
            headerName: '',
            width: 140,
            sortable: false,
            renderCell: (params) => (params.row.isAssigned ? (_jsx(Button, { variant: 'contained', color: 'success', startIcon: _jsx(CheckIcon, {}), sx: { textTransform: 'none', borderRadius: '12px' }, children: t('sponsorships.tickets.assigned') })) : (_jsx(Button, { variant: 'outlined', sx: { textTransform: 'none', borderRadius: '12px' }, onClick: () => handleAssign(params.row.id, params.row.type), children: t('sponsorships.tickets.assignLabel') })))
        }
    ];
    const handleAssign = (userId, type) => {
        if (!ticketId) {
            toast.error(t('sponsorships.tickets.assign.error.noTicketId') || 'Ticket ID is missing');
            return;
        }
        const request = {
            ticketId: ticketId,
            userId: userId,
            type: type
        };
        dispatch(assignTicketFromDatabaseAsync(request))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message || t('sponsorships.tickets.assign.success') || 'Ticket has successfully been assigned.');
                if (id) {
                    dispatch(getSuggestedPeopleForPartnerAsync({
                        partnerId: id,
                        eventId: eventId,
                        skip: 0,
                        take: null,
                        includePeople: true,
                        includeExternalRelations: true
                    }));
                }
            }
            else {
                toast.error(response.message || t('sponsorships.tickets.assign.error.failed') || 'Failed to assign ticket.');
            }
        })
            .catch((error) => {
            toast.error(t('sponsorships.tickets.assign.error.failed') || 'Failed to assign ticket.');
        });
        onClose();
    };
    return (_jsxs(Box, { sx: { p: 1 }, children: [_jsx(Box, { sx: { mb: 2 }, children: _jsx(StyledSearchInput, { sx: { width: '100%' }, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), handleResetCallback: () => setSearchTerm(''), ...{ placeholder: t('common.search') || 'Search' } }) }), _jsx(PrimaryGrid, { rows: ticketrelation ? ticketrelation.items : [], columns: columns, maxHeight: "calc(100dvh - 255px)", showFilters: false, showHeaderControls: false, showNewHeaderControls: true, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: ticketrelation?.totalCount || 0, pageSizeOptions: [5, 10, 25, 50], rowSelectionModel: selectedId ? [selectedId] : [], onSelectionChange: (sel) => {
                    const id = Array.isArray(sel) ? sel[0] ?? null : sel;
                    setSelectedId(id);
                }, getRowHeight: () => 85 })] }));
};
export default DatabaseGrid;
